/**
 * Terms of Service Content Dialog
 * Legal terms display for onboarding consent flow
 */

import { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { ScrollArea } from '../ui/scroll-area'
import { FileText } from 'lucide-react'

interface ToSContentProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ToSContent: FC<ToSContentProps> = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] p-0 gap-0 overflow-hidden bg-background/95 backdrop-blur-xl border-border/50">
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b border-border/50 bg-gradient-to-br from-clinical-sage/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-clinical-sage/20 border border-clinical-sage/30 flex items-center justify-center">
              <FileText className="h-6 w-6 text-clinical-sage" />
            </div>
            <div>
              <DialogTitle className="text-lg font-display font-semibold">
                Terms of Service
              </DialogTitle>
              <DialogDescription className="text-xs">
                Please review the terms governing use of Casey's Panel by Aclera-AI
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <ScrollArea className="h-[60vh]">
          <div className="p-6 prose prose-sm dark:prose-invert max-w-none">
            <p className="text-muted-foreground text-sm">
              Last updated: July 17, 2026
            </p>
            <p className="text-muted-foreground text-sm">
              These Terms of Service ("Terms") govern your access to and use of Casey's Panel by Aclera-AI,
              an AI-powered clinical documentation system ("Service"). By accessing or using the Service,
              you agree to be bound by these Terms.
            </p>

            <h3>1. Acceptance of Terms</h3>
            <p>
              By creating an account or using the Service, you acknowledge that you have read, understood,
              and agree to be bound by these Terms and our Privacy Policy. If you are using the Service on
              behalf of an organization, you represent that you have authority to bind that organization to
              these Terms.
            </p>
            <p>
              If you do not agree to these Terms, you may not access or use the Service.
            </p>

            <h3>2. Service Description</h3>
            <p>
              Casey's Panel by Aclera-AI provides AI-assisted clinical documentation tools for healthcare providers, including:
            </p>
            <ul>
              <li>Conversational interface for generating clinical documents (HPI, assessments, plans, handoffs)</li>
              <li>Integration with clinical knowledge bases and literature search</li>
              <li>Multi-patient workspace management</li>
              <li>Artifact generation and editing capabilities</li>
              <li>HIPAA-compliant data handling and storage</li>
            </ul>
            <p>
              The Service is designed to assist healthcare professionals and is not intended to replace
              professional medical judgment. All clinical decisions remain the sole responsibility of the
              licensed healthcare provider.
            </p>

            <h3>3. User Responsibilities</h3>
            <p>
              As a user of the Service, you agree to:
            </p>
            <ul>
              <li>Provide accurate and complete information during registration and use of the Service.</li>
              <li>Maintain the confidentiality of your account credentials and immediately notify us of any
              unauthorized access.</li>
              <li>Use the Service only for lawful purposes and in compliance with all applicable laws and
              regulations, including HIPAA.</li>
              <li>Review and verify all AI-generated content before use in clinical practice.</li>
              <li>Ensure appropriate patient consent for any PHI entered into the Service.</li>
              <li>Comply with your organization's policies regarding the use of AI tools in clinical settings.</li>
            </ul>

            <h3>4. Prohibited Uses</h3>
            <p>
              You agree not to:
            </p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of these Terms.</li>
              <li>Attempt to gain unauthorized access to the Service or its related systems.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Transmit any viruses, malware, or other malicious code through the Service.</li>
              <li>Use the Service to store or transmit content that violates any third party's rights.</li>
              <li>Reverse engineer, decompile, or disassemble any portion of the Service.</li>
              <li>Share your account credentials with unauthorized individuals.</li>
              <li>Use the Service in a manner that could harm patients or the public health.</li>
            </ul>

            <h3>5. Intellectual Property and De-identified Data</h3>
            <p>
              The Service and its original content (excluding user-provided content), features, and functionality
              are owned by Aclera-AI and are protected by international copyright, trademark, patent, trade
              secret, and other intellectual property laws.
            </p>
            <p>
              You retain ownership of any content you submit to the Service ("Customer Data"). By using the
              Service, you grant us a license to use, process, and store Customer Data to provide, maintain,
              secure, and improve the Service.
            </p>
            <p>
              We may create de-identified data from Customer Data as permitted by the Business Associate
              Agreement, using the HIPAA Safe Harbor de-identification standard (45 CFR §164.514(b)(2)).
              As between you and Aclera-AI, Aclera-AI owns De-identified Data and may use it for any lawful
              purpose, including training and improving our AI models. De-identified Data contains no patient
              or user identifiers and survives termination of these Terms. With respect to Protected Health
              Information, the Business Associate Agreement controls over these Terms.
            </p>

            <h3>6. Disclaimer of Warranties</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that:
            </p>
            <ul>
              <li>The Service will be uninterrupted, timely, secure, or error-free.</li>
              <li>The results obtained from use of the Service will be accurate or reliable.</li>
              <li>The quality of any information or content obtained through the Service will meet your expectations.</li>
              <li>Any AI-generated content will be clinically accurate or appropriate for any specific patient.</li>
            </ul>
            <p>
              AI-generated clinical content is intended as a decision support tool only. Healthcare providers
              must exercise independent professional judgment and are solely responsible for all clinical decisions.
            </p>

            <h3>7. Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ACLERA-AI, ITS DIRECTORS,
              EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA,
              USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul>
              <li>Your access to or use of (or inability to access or use) the Service.</li>
              <li>Any conduct or content of any third party on the Service.</li>
              <li>Any content obtained from the Service.</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
              <li>Clinical decisions made based on AI-generated content.</li>
            </ul>

            <h3>8. Termination</h3>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior
              notice or liability, for any reason, including without limitation if you breach these Terms.
            </p>
            <p>
              Upon termination:
            </p>
            <ul>
              <li>Your right to use the Service will immediately cease.</li>
              <li>We may delete your account and associated data in accordance with our data retention policies
              and applicable law. Our deletion obligations do not apply to De-identified Data, which contains
              no patient or user identifiers.</li>
              <li>Provisions of these Terms that by their nature should survive termination shall survive.</li>
            </ul>
            <p>
              You may terminate your account at any time by contacting us or through the account settings.
            </p>

            <h3>9. Governing Law</h3>
            <p>
              These Terms shall be governed and construed in accordance with the laws of the State of Delaware,
              United States, without regard to its conflict of law provisions.
            </p>
            <p>
              Any disputes arising out of or relating to these Terms or the Service shall be resolved through
              binding arbitration in accordance with the rules of the American Arbitration Association, except
              that either party may seek injunctive or other equitable relief in any court of competent jurisdiction.
            </p>

            <h3>10. Changes to Terms</h3>
            <p>
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If a
              revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.
            </p>
            <p>
              Your continued use of the Service after any such changes constitutes your acceptance of the new
              Terms.
            </p>

            <h3>11. Contact Information</h3>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              Email: <a href="mailto:legal@aclera-ai.com" className="text-clinical-sage hover:underline">legal@aclera-ai.com</a>
            </p>

            <div className="mt-8 p-4 rounded-lg bg-clinical-sage/10 border border-clinical-sage/20">
              <p className="text-xs text-muted-foreground m-0">
                By using Casey's Panel by Aclera-AI, you acknowledge that you have read, understood, and agree to be bound
                by these Terms of Service and our Business Associate Agreement for HIPAA compliance.
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border/50 bg-surface/30">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-xs"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}


